// src/quality-management/components/project-form/ProjectFormFields.tsx
// Campos do formulário de projeto, compartilhados entre criar e editar.
// Os dados de gestão/financeiros ficam num bloco recolhível para um cadastro rápido.

import React, { useState } from 'react';
import { User, ProjectStatus } from '../../services/api/types';
import { Input } from '../../../shared/components/ui/Input';
import { Select } from '../../../shared/components/ui/Select';
import { Textarea } from '../../../shared/components/ui/Textarea';
import { Spinner } from '../../../shared/components/ui/Spinner';
import { ProjectFormState, STATUS_OPTIONS } from './projectForm';

interface ProjectFormFieldsProps {
  form: ProjectFormState;
  update: (patch: Partial<ProjectFormState>) => void;
  managers: User[];
  employees: User[];
  loadingEmployees: boolean;
  disabled?: boolean;
  error?: string;
  // Quando true, o bloco de dados de gestão já vem expandido (usado na edição)
  defaultExpanded?: boolean;
}

const ProjectFormFields: React.FC<ProjectFormFieldsProps> = ({
  form,
  update,
  managers,
  employees,
  loadingEmployees,
  disabled,
  error,
  defaultExpanded = false,
}) => {
  const [showAdvanced, setShowAdvanced] = useState(defaultExpanded);

  const toggleEmployee = (employeeId: number) => {
    update({
      selectedEmployees: form.selectedEmployees.includes(employeeId)
        ? form.selectedEmployees.filter((id) => id !== employeeId)
        : [...form.selectedEmployees, employeeId],
    });
  };

  return (
    <div className="flex flex-col gap-5">
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-danger/30 bg-danger/10 px-3.5 py-2.5 text-sm text-danger">
          {error}
        </div>
      )}

      {/* Essencial */}
      <Input
        id="project-name"
        name="project-name"
        label="Nome do projeto *"
        placeholder="Digite o nome do projeto"
        value={form.name}
        onChange={(e) => update({ name: e.target.value })}
        required
        disabled={disabled}
        data-testid="project-name-input"
      />

      <Textarea
        id="project-description"
        name="project-description"
        label="Descrição"
        placeholder="Descreva os objetivos e requisitos do projeto"
        value={form.description}
        onChange={(e) => update({ description: e.target.value })}
        rows={3}
        disabled={disabled}
        data-testid="project-description-input"
      />

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Select
          id="project-pm"
          name="project-pm"
          label="PM (gerente do projeto)"
          placeholder="Selecione o PM"
          value={form.pmId}
          onChange={(e) => update({ pmId: e.target.value })}
          disabled={disabled}
          data-testid="project-pm-select"
          options={(managers || []).map((m) => ({ value: m.id, label: m.name }))}
        />
        <Select
          id="project-status"
          name="project-status"
          label="Status"
          value={form.status}
          onChange={(e) => update({ status: e.target.value as ProjectStatus })}
          disabled={disabled}
          data-testid="project-status-select"
          options={STATUS_OPTIONS}
        />
      </div>

      {/* Equipe */}
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-foreground">Atribuir funcionários</span>
        {loadingEmployees ? (
          <div className="flex items-center gap-2 py-3 text-sm text-foreground-muted">
            <Spinner size="sm" /> Carregando funcionários...
          </div>
        ) : employees && Array.isArray(employees) && employees.length > 0 ? (
          <div className="max-h-44 overflow-y-auto rounded-lg border border-border">
            {employees.map((employee) => {
              // assigned_employee_ids usa o ID da tabela employees, não o ID do usuário
              const assignId = employee.employee_id ?? employee.id;
              return (
                <label
                  key={employee.id}
                  htmlFor={`employee-${employee.id}`}
                  className="flex cursor-pointer items-center gap-3 border-b border-border/60 px-3.5 py-2.5 text-base text-foreground last:border-0 hover:bg-surface-2"
                >
                  <input
                    type="checkbox"
                    id={`employee-${employee.id}`}
                    checked={form.selectedEmployees.includes(assignId)}
                    onChange={() => toggleEmployee(assignId)}
                    disabled={disabled}
                    className="h-4 w-4 accent-brand"
                  />
                  {employee.name}
                </label>
              );
            })}
          </div>
        ) : (
          <p className="rounded-lg border border-border bg-surface-2 px-3.5 py-3 text-sm text-foreground-muted">
            Nenhum funcionário disponível
          </p>
        )}
      </div>

      {/* Dados de gestão (recolhível) */}
      <div className="rounded-xl border border-border">
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          className="flex w-full items-center justify-between gap-2 rounded-xl px-4 py-3 text-left text-sm font-medium text-foreground transition-colors hover:bg-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/40"
          data-testid="project-advanced-toggle"
          aria-expanded={showAdvanced}
        >
          <span className="flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 3v18h18" />
              <path d="m19 9-5 5-4-4-3 3" />
            </svg>
            Dados de gestão (opcional)
          </span>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>

        {showAdvanced && (
          <div className="flex flex-col gap-5 border-t border-border p-4">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
              <Input
                id="project-number"
                name="project-number"
                type="number"
                label="Número do projeto"
                placeholder="Ex.: 1024"
                value={form.projectNumber}
                onChange={(e) => update({ projectNumber: e.target.value })}
                disabled={disabled}
                data-testid="project-number-input"
              />
              <Input
                id="project-client"
                name="project-client"
                label="Cliente"
                placeholder="Nome do cliente"
                value={form.client}
                onChange={(e) => update({ client: e.target.value })}
                disabled={disabled}
                containerClassName="sm:col-span-2"
                data-testid="project-client-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-5 sm:grid-cols-4">
              <Input
                id="project-value"
                name="project-value"
                type="number"
                step="0.01"
                label="Valor (R$)"
                placeholder="0,00"
                value={form.value}
                onChange={(e) => update({ value: e.target.value })}
                disabled={disabled}
                data-testid="project-value-input"
              />
              <Input
                id="project-hours"
                name="project-hours"
                type="number"
                step="0.5"
                label="Horas"
                placeholder="0"
                value={form.hours}
                onChange={(e) => update({ hours: e.target.value })}
                disabled={disabled}
                data-testid="project-hours-input"
              />
              <Input
                id="project-margin"
                name="project-margin"
                type="number"
                step="0.01"
                label="Margem (%)"
                placeholder="0"
                value={form.margin}
                onChange={(e) => update({ margin: e.target.value })}
                disabled={disabled}
                data-testid="project-margin-input"
              />
              <Input
                id="project-ppi"
                name="project-ppi"
                type="number"
                step="0.01"
                label="PPI"
                placeholder="0"
                value={form.ppi}
                onChange={(e) => update({ ppi: e.target.value })}
                disabled={disabled}
                data-testid="project-ppi-input"
              />
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
              <Input
                id="project-remaining-time"
                name="project-remaining-time"
                type="number"
                step="0.5"
                label="Tmp. rest. (h)"
                placeholder="0"
                value={form.remainingTime}
                onChange={(e) => update({ remainingTime: e.target.value })}
                disabled={disabled}
                data-testid="project-remaining-time-input"
              />
              <Input
                id="project-technical-progress"
                name="project-technical-progress"
                type="number"
                step="0.01"
                label="Prog. técnico (%)"
                placeholder="0"
                value={form.technicalProgress}
                onChange={(e) => update({ technicalProgress: e.target.value })}
                disabled={disabled}
                data-testid="project-technical-progress-input"
              />
              <Input
                id="project-financial-progress"
                name="project-financial-progress"
                type="number"
                step="0.01"
                label="Prog. financeiro (%)"
                placeholder="0"
                value={form.financialProgress}
                onChange={(e) => update({ financialProgress: e.target.value })}
                disabled={disabled}
                data-testid="project-financial-progress-input"
              />
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Input
                id="project-start-date"
                name="project-start-date"
                type="datetime-local"
                label="Início (data e hora)"
                value={form.startDate}
                onChange={(e) => update({ startDate: e.target.value })}
                disabled={disabled}
                data-testid="project-start-date-input"
              />
              <Input
                id="project-end-date"
                name="project-end-date"
                type="datetime-local"
                label="Fim (data e hora)"
                value={form.endDate}
                onChange={(e) => update({ endDate: e.target.value })}
                disabled={disabled}
                data-testid="project-end-date-input"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectFormFields;
